import { AxiosInstance } from 'axios';
import { IQuoteParams } from './interfaces/props';
import dayjs from 'dayjs';
import { IGeneralStatsResponse } from './interface';
import {
  IMovableWindowsParams,
  IWindowsAccumulated,
  IWindowsAccumulatedResponse,
  IWindowsSuccessRateParams,
  ISuccessRateResponse,
} from './interfaces/windows';

export class MicroservicesAPI {
  private baseUrl: string;

  constructor(public readonly http: AxiosInstance) {
    this.baseUrl = this.http.defaults.baseURL || '';
  }

  setVersion(version: string = 'v3') {
    if (!this.baseUrl) {
      throw new Error('Base URL is not set');
    }

    this.baseUrl = this.baseUrl!.replace(/\/v\d+/, `/${version}`);

    return this;
  }

  setDomain(domain: string) {
    this.baseUrl = this.baseUrl!.replace(/\/api\d*/, `/${domain}`);

    return this;
  }

  public stats = {
    getStats: async ({
      identifier,
      startDate,
      endDate,
      format_decimal = false,
      details = false,
      currency,
      ...config
    }: IQuoteParams) => {
      const params = {
        currency,
        format_decimal,
        ...(startDate && {
          start_date: dayjs.utc(startDate).valueOf(),
        }),
        ...(endDate && {
          end_date: dayjs.utc(endDate).valueOf(),
        }),
        ...(details && { details }),
      };

      const response = await this._makeRequest<IGeneralStatsResponse>({
        endpoint: `/stats/${identifier}`,
        method: 'GET',
        params,
        ...config,
      });

      if (!response) return null;
      return { identifier, ...response };
    },
  };

  public windows = {
    getQuote: async ({
      identifier,
      safe = false,
      ...params
    }: IMovableWindowsParams): Promise<IWindowsAccumulated | null> => {
      const response = await this._makeRequest<IWindowsAccumulatedResponse>({
        endpoint: `/rolling-windows/accumulated-return/${identifier}`,
        method: 'GET',
        safe,
        params,
      });

      if (response) {
        return { ...response, identifier };
      }

      return null;
    },
    getSuccessRate: async ({
      identifier,
      benchmark,
      ...params
    }: IWindowsSuccessRateParams) => {
      const response = await this._makeRequest<ISuccessRateResponse>({
        endpoint: `/rolling-windows/success-rate/${identifier}`,
        method: 'GET',
        params: { ...params, benchmark },
      });

      return { ...response, benchmark };
    },
  };

  private async _makeRequest<T>({
    endpoint,
    method,
    body = null,
    params = null,
    headers = null,
    safe = false,
    config = null,
  }: Params): Promise<T> {
    try {
      // Se nenhum header Authorization foi informado, tenta obtê-lo dinamicamente
      // if (!headers?.Authorization && !headers?.authorization) {
      //   try {
      //     const { getAccessToken } = await import('@core/features-core/auth');
      //     const token = await getAccessToken();
      //     if (token) {
      //       headers = { ...(headers || {}), Authorization: `Bearer ${token}` };
      //     }
      //   } catch (_) {
      //     // Falha ao obter token – segue sem Authorization
      //   }
      // }

      const { data } = await this.http({
        method: method,
        url: `${this.baseUrl}${endpoint}`,
        ...(body && { data: body }),
        ...(params && { params }),
        ...(headers && { headers }),
        ...(config && { ...config }),
      });
      if (data === '') return null as T;

      return data;
    } catch (error: any) {
      const requestError = {
        url: error.config.url,
        method: error.config.method,
        status: error.response?.status,
        data: error.response?.data,
      };

      console.error(
        'MICROSERVICES: Error API:',
        error?.request?._header,
        requestError
      );

      if (safe) return null as T;
      if (error.response?.data)
        throw {
          ...error.response?.data,
          data: error?.response?.data,
          status: error.response.status,
        };

      throw error;
    }
  }
}

interface Params {
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: any;
  params?: any;
  headers?: any;
  safe?: boolean; // Se true, o método não irá lançar exceção caso não encontre cotação para o ativo, retornando null
  config?: any;
}
